import type { FlowDto } from '@/types/dtos/flows/response/flow.dto';
import type { ProcessingInsightDto } from '@/types/dtos/media/processing-insight.dto';
import type { FileType } from '@/types/dtos/media/media.asset.dto';
import type { MediaAssetDto } from '@/types/dtos/media/media.asset.dto';
import type { MediaDto } from '@/types/dtos/media/media.dto';
import type { TranslationKey } from '@/i18n/translation-key';
import { hasProcessingAdvisories } from '@/helpers/media-processing-policy-messages.helper';

const PREPROCESSABLE_FILE_TYPES = new Set<FileType>(['presentation', 'pdf']);

export function isPreprocessableMediaFileType(fileType?: FileType): boolean {
  return !!fileType && PREPROCESSABLE_FILE_TYPES.has(fileType);
}

export function getLatestApprovedAsset(assets: MediaAssetDto[]): MediaAssetDto | undefined {
  return [...assets].filter((asset) => asset.isApproved).sort((a, b) => b.version - a.version)[0];
}

/** Highest approved asset that is playout-ready; falls back to latest approved (matches backend playout resolution). */
export function getLatestPlayableApprovedAsset(assets: MediaAssetDto[]): MediaAssetDto | undefined {
  const approved = assets.filter((asset) => asset.isApproved).sort((a, b) => b.version - a.version);
  const playable = approved.find((asset) => asset.processingInsight?.playoutReady === true);
  if (playable) {
    return playable;
  }
  return approved[0];
}

/** Whether an asset can be opened in the media preview (matches backend playout/preview requirements). */
export function isAssetPreviewable(asset: MediaAssetDto): boolean {
  return isAssetApprovable(asset);
}

/** Whether an operator may approve this asset version (matches backend playout readiness). */
export function isAssetApprovable(asset: MediaAssetDto): boolean {
  if (!asset.fileType || asset.uploadStatus === 'uploading') {
    return false;
  }
  if (!isPreprocessableMediaFileType(asset.fileType)) {
    return true;
  }
  const insight = asset.processingInsight;
  if (!insight || insight.readiness === 'rejected' || insight.readiness === 'failed') {
    return false;
  }
  return insight.playoutReady === true;
}

/** Whether an asset may be auto-approved after upload or when playout becomes ready. */
export function isAssetAutoApprovable(asset: MediaAssetDto): boolean {
  return isAssetApprovable(asset) && !hasProcessingAdvisories(asset.processingInsight);
}

/**
 * Resolve which asset version control-preview would use when no explicit version is passed.
 * Mirrors backend {@link resolveAssetVersionForPlayoutPreview} but only returns previewable assets.
 */
export function resolvePreviewAssetForControl(assets: MediaAssetDto[]): MediaAssetDto | undefined {
  if (assets.length === 0) {
    return undefined;
  }
  const playable = getLatestPlayableApprovedAsset(assets);
  if (playable && isAssetPreviewable(playable)) {
    return playable;
  }
  const sorted = [...assets].sort((a, b) => b.version - a.version);
  const fallback = sorted.find((asset) => asset.isApproved) ?? sorted[0];
  return fallback && isAssetPreviewable(fallback) ? fallback : undefined;
}

export function hasPreviewableMedia(assets: MediaAssetDto[]): boolean {
  return resolvePreviewAssetForControl(assets) !== undefined;
}

export function mediaHasPlayablePresentationSlides(media: MediaDto | undefined): boolean {
  if (!media) {
    return false;
  }
  const asset = getLatestPlayableApprovedAsset(media.assets ?? []);
  return (
    asset?.fileType === 'presentation' &&
    asset.processingInsight?.playoutReady === true &&
    asset.processingInsight?.readiness !== 'rejected'
  );
}

export function getProcessingInsightForMediaItem(media: MediaDto): ProcessingInsightDto | undefined {
  const approved = getLatestApprovedAsset(media.assets ?? []);
  if (approved?.processingInsight) {
    return approved.processingInsight;
  }

  const latest = [...(media.assets ?? [])].sort((a, b) => b.version - a.version)[0];
  return latest ? getProcessingInsightForAsset(latest) : undefined;
}

export function getProcessingInsightForAsset(asset: MediaAssetDto): ProcessingInsightDto | undefined {
  if (!isPreprocessableMediaFileType(asset.fileType)) {
    return undefined;
  }
  if (asset.uploadStatus === 'uploading' || asset.uploadStatus === 'failed') {
    return undefined;
  }
  return asset.processingInsight;
}

export function isMediaBlockedForPerform(media: MediaDto): {
  blocked: boolean;
  reasonKey?: TranslationKey;
} {
  const insight = getProcessingInsightForMediaItem(media);
  if (!insight || insight.readiness === 'not_applicable') {
    return { blocked: false };
  }
  if (insight.readiness === 'ok' || insight.playoutReady) {
    return { blocked: false };
  }
  if (insight.readiness === 'rejected') {
    return { blocked: true, reasonKey: 'media.processing.blockPerformRejected' };
  }
  if (insight.readiness === 'failed') {
    return { blocked: true, reasonKey: 'media.processing.blockPerformProcessing' };
  }
  return { blocked: true, reasonKey: 'media.processing.blockPerformProcessing' };
}

export type StagedMediaProcessingBlock = {
  blocked: boolean;
  mediaTitle?: string;
  reasonKey?: TranslationKey;
  insight?: ProcessingInsightDto;
};

export function collectFlowMedia(flow: FlowDto): MediaDto[] {
  const result: MediaDto[] = [];
  const seen = new Set<string>();
  const add = (items?: MediaDto[]) => {
    for (const item of items ?? []) {
      if (!seen.has(item.id)) {
        seen.add(item.id);
        result.push(item);
      }
    }
  };
  add(flow.media);
  for (const slot of flow.slots ?? []) {
    if (slot.content && typeof slot.content === 'object' && 'media' in slot.content) {
      add(slot.content.media as MediaDto[]);
    }
  }
  return result;
}

export function findStagedMediaProcessingBlock(
  selectedItemIds: string[],
  mediaItems: MediaDto[],
): StagedMediaProcessingBlock {
  const byId = new Map(mediaItems.map((media) => [media.id, media]));
  for (const itemId of selectedItemIds) {
    if (typeof itemId !== 'string' || !itemId.startsWith('media:')) {
      continue;
    }
    const media = byId.get(itemId.slice('media:'.length));
    if (!media) {
      continue;
    }
    const block = isMediaBlockedForPerform(media);
    if (block.blocked) {
      return {
        blocked: true,
        mediaTitle: media.title,
        reasonKey: block.reasonKey,
        insight: getProcessingInsightForMediaItem(media),
      };
    }
  }
  return { blocked: false };
}
